'use client';

import type { DeviceType } from '@weberus/shared-types';
import { type FC, type ReactNode, useRef, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { cn } from '@yanolja-rnd/nol-design-system';
import { throttle } from 'lodash-es';

interface AccordionSelectBoxProps {
  isOpen?: boolean;
  deviceType?: DeviceType;
  children: {
    header: ReactNode;
    additonalHeader?: ReactNode;
    body: ReactNode;
  };
  className?: string;
  onHeaderClick?: () => void;
}

/**
 * 클릭 시 펼쳐지는 아코디언 박스
 *
 * - motion 사용하여 부드러운 효과를 내도록 함
 * - 추후 이와 비슷한 컴포넌트를 사용 할 시 공통 컴포넌트로 사용 가능
 */
export const AccordionSelectBox: FC<AccordionSelectBoxProps> = (props) => {
  const { isOpen = false, children, className, deviceType, onHeaderClick } = props;
  const { header, additonalHeader, body } = children;
  const isPc = deviceType === 'etc';

  const [isScrolled, setIsScrolled] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);

  const handleScroll = throttle(() => {
    if (bodyRef.current) {
      setIsScrolled(bodyRef.current.scrollTop > 0);
    }
  }, 100);

  useEffect(() => {
    const bodyElement = bodyRef.current;
    if (!bodyElement) return;

    bodyElement.addEventListener('scroll', handleScroll);

    return () => {
      bodyElement.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  return (
    <div
      className={cn(
        'relative flex max-h-full flex-col overflow-hidden rounded-12 bg-static-white shadow-2',
        isPc ? 'min-h-60' : 'min-h-56',
        className,
      )}>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: isOpen ? 1 : 0 }}
        transition={{ opacity: { duration: 0.2, ease: 'easeInOut' } }}
        className="pointer-events-none absolute inset-0 z-10 rounded-12 border border-line-neutral-main"
      />
      <div className="relative flex flex-col overflow-hidden rounded-12">
        <div
          className={cn({ 'z-10 shadow-2': isScrolled })}
          style={{
            clipPath: isOpen ? 'inset(-4px 1px)' : undefined,
          }}>
          <div
            className={cn(
              'flex shrink-0 cursor-pointer flex-col items-center rounded-12 border border-transparent',
              isPc ? 'h-60 px-20' : 'h-56 px-16',
              {
                'border-line-neutral-weak3': !isOpen,
              },
            )}
            onClick={onHeaderClick}>
            {header}
          </div>
          <AnimatePresence initial={false}>
            {!!additonalHeader && isOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2, ease: 'easeInOut' }}>
                {additonalHeader}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <AnimatePresence initial={false}>
          {isOpen && (
            <motion.div
              ref={bodyRef}
              key="body"
              initial="collapsed"
              animate="open"
              exit="collapsed"
              transition={{ duration: 0.2, ease: 'easeInOut' }}
              className="overflow-y-auto overscroll-y-none"
              variants={{
                open: { opacity: 1, height: 'auto' },
                collapsed: { opacity: 0, height: 0 },
              }}>
              {body}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};
